// src/components/HomeGoalTrackerPreview.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const PreviewContainer = styled.div`
  padding: 20px;
  background-color: var(--secondaryBackgroundColor);
  border-radius: 14px;
  color: var(--fontPrimaryColor);
  height: 100%;
  max-height: 600px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
`;

const Header = styled.h1`
  font-size: 20px;
  font-weight: 500;
  text-align: center;
  margin-top: 1em;
  margin-bottom: 1em;
  color: var(--fontPrimaryColor);
`;

const GoalsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
  margin-bottom: 10px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
`;

const GoalRow = styled.div`
  background: var(--thirdBackgroundColor);
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 10px;
  opacity: ${(props) => (props.$clickable ? 1 : 0.7)};
  pointer-events: ${(props) => (props.$clickable ? "auto" : "none")};
`;

const GoalTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 5px;
  color: var(--fontPrimaryColor);
`;

const GoalType = styled.div`
  font-size: 0.8rem;
  color: var(--fontSecondaryColor);
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: var(--secondaryBackgroundColor);
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background: ${(props) => {
    if (props.$percentage >= 100) return "#4caf50";
    if (props.$percentage >= 75) return "#63d142";
    if (props.$percentage >= 50) return "#ff9800";
    return "#f44336";
  }};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: var(--fontSecondaryColor);
`;

const AddButton = styled.button`
  margin-top: 20px;
  background: #495269;
  color: white;
  border: none;
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.5;
  pointer-events: none;

  &:hover {
    background: #717d9b;
  }
`;

const BlurOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: ${(props) => (props.$active ? "blur(8px)" : "none")};
  background: ${(props) =>
    props.$active ? "var(--lowBackgroundColor)" : "transparent"};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${(props) => (props.$active ? 10 : -1)};
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: all 0.3s ease;
`;

const UpgradeMessage = styled.div`
  background: var(--primaryColor);
  color: white;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 80%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 11;

  h3 {
    margin: 0 0 10px 0;
    font-size: 1.2rem;
  }

  p {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }

  button {
    background: white;
    color: var(--primaryColor);
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  }
`;

const HomeGoalTrackerPreview = () => {
  const { t } = useTranslation();
  const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Pre-set demo goals for FREE users
  const demoGoals = [
    {
      id: 1,
      title: "Save for Vacation",
      type: "Financial",
      currentValue: 1640,
      targetValue: 2000,
      percentage: 82,
    },
    {
      id: 2,
      title: "Complete 50 Tasks",
      type: "Task",
      currentValue: 32,
      targetValue: 50,
      percentage: 64,
    },
    {
      id: 3,
      title: "Learn New Language",
      type: "Milestone",
      currentValue: 3,
      targetValue: 5,
      percentage: 60,
      milestones: [
        { title: "Learn basic phrases", completed: true },
        { title: "Complete first course", completed: true },
        { title: "Hold basic conversation", completed: true },
        { title: "Watch movie without subtitles", completed: false },
        { title: "Travel and use language", completed: false },
      ],
    },
    {
      id: 4,
      title: "Exercise 30 Days",
      type: "Habit",
      currentValue: 18,
      targetValue: 30,
      percentage: 60,
    },
  ];

  const handleContainerClick = () => {
    setShowUpgradeMessage(true);
  };

  const handleUpgradeClick = () => {
    window.location.href = "/upgrade";
  };

  const handleCloseUpgrade = (e) => {
    e.stopPropagation();
    setShowUpgradeMessage(false);
    setHovering(false);
  };

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    if (!showUpgradeMessage) {
      setHovering(false);
    }
  };

  return (
    <PreviewContainer
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Header>{t("goals.title")}🔒</Header>

      <GoalsList>
        {demoGoals.map((goal) => (
          <GoalRow key={goal.id} $clickable={false}>
            <GoalTitle>{goal.title}</GoalTitle>
            <GoalType>{goal.type} Goal</GoalType>

            <ProgressBar>
              <ProgressFill $percentage={goal.percentage} />
            </ProgressBar>

            <ProgressText>
              <span>
                {goal.currentValue} / {goal.targetValue}
                {goal.type === "Milestone" ? " milestones" : ""}
              </span>
              <span>{goal.percentage}%</span>
            </ProgressText>

            {goal.milestones && (
              <div style={{ marginTop: "10px", fontSize: "0.8rem" }}>
                {goal.milestones.slice(0, 2).map((milestone, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "5px",
                      opacity: milestone.completed ? 0.6 : 1,
                    }}
                  >
                    <span
                      style={{
                        color: milestone.completed ? "#4caf50" : "#666",
                      }}
                    >
                      {milestone.completed ? "✓" : "○"}
                    </span>
                    <span
                      style={{
                        textDecoration: milestone.completed
                          ? "line-through"
                          : "none",
                        color: milestone.completed
                          ? "#aaa"
                          : "var(--fontPrimaryColor)",
                      }}
                    >
                      {milestone.title}
                    </span>
                  </div>
                ))}
                {goal.milestones.length > 2 && (
                  <div
                    style={{
                      color: "#aaa",
                      textAlign: "center",
                      padding: "5px",
                    }}
                  >
                    +{goal.milestones.length - 2} more steps
                  </div>
                )}
              </div>
            )}
          </GoalRow>
        ))}
      </GoalsList>

      <AddButton>Add New Goal (Upgrade to PRO)</AddButton>

      <BlurOverlay $active={showUpgradeMessage || hovering}>
        {showUpgradeMessage ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>🔒 Goal Tracker Pro</h3>
            <p>
              Track and manage unlimited goals with smart progress tracking,
              milestone planning, and automatic syncing with other features.
            </p>
            <button onClick={handleUpgradeClick}>Upgrade to PRO</button>
            <button
              onClick={handleCloseUpgrade}
              style={{
                background: "transparent",
                color: "white",
                marginTop: "10px",
                fontSize: "0.85rem",
              }}
            >
              Maybe later
            </button>
          </UpgradeMessage>
        ) : hovering ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>This is a feature for the Pro Users Only</h3>
            <p>
              This is a preview of Goal Tracker. Upgrade to PRO to unlock full
              functionality.
            </p>
            <Link to="/upgrade" style={{ textDecoration: "none" }}>
              <button>Upgrade Now</button>
            </Link>
          </UpgradeMessage>
        ) : null}
      </BlurOverlay>
    </PreviewContainer>
  );
};

export default HomeGoalTrackerPreview;
